import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import DataTable from '../components/common/DataTable';
import '../styles/LocationPage.css';
import { getOpenRequests, getActiveWorkersMap } from '../utils/api';
import {
  MapPin,
  Navigation,
  Users,
  Briefcase,
  Clock,
  Activity,
  Star,
  RefreshCw,
  Radio,
  X
} from 'lucide-react';

// Centro de Luanda (fallback quando só há 1 ponto)
const LUANDA = { lat: -8.8390, lng: 13.2894 };

const LocationPage = () => {
  const [activeTab, setActiveTab] = useState('map');
  const [selectedWorker, setSelectedWorker] = useState(null);

  const { data: requests } = useApi(getOpenRequests, { immediate: true });
  const { data: mapData, isLoading, execute: refreshMap } = useApi(getActiveWorkersMap, { immediate: true });

  // Auto-refresh do mapa a cada 30s
  useEffect(() => {
    const t = setInterval(() => refreshMap(), 30000);
    return () => clearInterval(t);
  }, [refreshMap]);

  const workers = mapData?.workers || [];

  // Projeção: calcula limites reais dos pontos (com margem) e converte lat/lng em %
  const lats = workers.map(w => Number(w.latitude)).filter(n => !isNaN(n));
  const lngs = workers.map(w => Number(w.longitude)).filter(n => !isNaN(n));
  const pad = 0.02; // ~2 km de margem
  const minLat = lats.length ? Math.min(...lats) - pad : LUANDA.lat - 0.1;
  const maxLat = lats.length ? Math.max(...lats) + pad : LUANDA.lat + 0.1;
  const minLng = lngs.length ? Math.min(...lngs) - pad : LUANDA.lng - 0.1;
  const maxLng = lngs.length ? Math.max(...lngs) + pad : LUANDA.lng + 0.1;
  const toXY = (lat, lng) => ({
    x: ((Number(lng) - minLng) / (maxLng - minLng || 1)) * 90 + 5,   // 5%–95%
    y: (1 - (Number(lat) - minLat) / (maxLat - minLat || 1)) * 85 + 7,
  });

  const tabs = [
    { id: 'map', label: 'Mapa', icon: MapPin },
    { id: 'workers', label: 'Profissionais', icon: Users },
    { id: 'requests', label: 'Pedidos', icon: Activity },
  ];

  const statusColors = {
    online: 'bg-green-500',
    away: 'bg-yellow-500',
    offline: 'bg-gray-500',
  };
  const statusLabels = { online: 'Online', away: 'Ausente', offline: 'Offline' };

  const workerColumns = [
    { key: 'user_id', label: 'ID', width: '60px' },
    { key: 'full_name', label: 'Nome' },
    { key: 'connection_status', label: 'Estado', render: (row) => (
      <div className="flex items-center gap-2">
        <div className={`w-2.5 h-2.5 rounded-full ${statusColors[row.connection_status] || 'bg-gray-500'}`} />
        <span className="text-sm text-gray-300">{statusLabels[row.connection_status] || row.connection_status}</span>
      </div>
    )},
    { key: 'average_rating', label: 'Avaliação', render: (row) => (
      <div className="flex items-center gap-1 text-gray-300">
        <Star className="w-3.5 h-3.5 text-yellow-400" />
        <span className="text-xs">{row.average_rating ? Number(row.average_rating).toFixed(1) : '—'}</span>
      </div>
    )},
    { key: 'coords', label: 'Coordenadas', render: (row) => (
      <div className="flex items-center gap-1 text-gray-400">
        <MapPin className="w-3.5 h-3.5" />
        <span className="text-xs">{Number(row.latitude).toFixed(4)}, {Number(row.longitude).toFixed(4)}</span>
      </div>
    )},
    { key: 'last_updated', label: 'Última Atualização', render: (row) => (
      <div className="flex items-center gap-1 text-gray-400">
        <Clock className="w-3.5 h-3.5" />
        <span className="text-xs">{row.last_updated ? new Date(row.last_updated).toLocaleString('pt-PT') : '—'}</span>
      </div>
    )},
  ];

  const requestColumns = [
    { key: 'request_id', label: 'ID', width: '60px' },
    { key: 'client_name', label: 'Cliente' },
    { key: 'service_type', label: 'Serviço' },
    { key: 'address', label: 'Morada', render: (row) => (
      <div className="flex items-center gap-1 text-gray-400">
        <MapPin className="w-3.5 h-3.5" />
        <span className="text-xs">{row.address || 'Luanda'}</span>
      </div>
    )},
    { key: 'status', label: 'Status', render: (row) => (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
        {row.status === 'open' ? 'Aberta' : row.status}
      </span>
    )},
    { key: 'created_at', label: 'Criada', render: (row) =>
      row.created_at ? new Date(row.created_at).toLocaleDateString('pt-PT') : '-'
    },
  ];

  return (
    <div className="location-admin-page space-y-6">
      <section className="location-admin-hero">
        <div>
          <span className="location-admin-eyebrow"><Radio className="w-4 h-4" /> Cobertura em tempo real</span>
          <h1>Geolocalização</h1>
          <p>Monitorize profissionais no terreno e pedidos abertos em Luanda, com atualização automática a cada 30 segundos.</p>
        </div>
        <button onClick={() => refreshMap()}
          className="location-refresh-button">
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </section>

      {/* Stats */}
      <div className="location-admin-stats grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="location-stat location-stat--green">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg"><Users className="w-5 h-5 text-green-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{mapData?.online ?? 0}</p>
              <p className="text-gray-400 text-sm">Online agora</p>
            </div>
          </div>
        </div>
        <div className="location-stat location-stat--amber">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-lg"><Briefcase className="w-5 h-5 text-yellow-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{mapData?.away ?? 0}</p>
              <p className="text-gray-400 text-sm">Ausentes (&lt;30 min)</p>
            </div>
          </div>
        </div>
        <div className="location-stat location-stat--blue">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg"><MapPin className="w-5 h-5 text-blue-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{(requests || []).filter(r => r.status === 'open').length}</p>
              <p className="text-gray-400 text-sm">Pedidos abertos</p>
            </div>
          </div>
        </div>
        <div className="location-stat location-stat--violet">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg"><Navigation className="w-5 h-5 text-purple-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{mapData?.total ?? 0}</p>
              <p className="text-gray-400 text-sm">Total Rastreados</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="location-admin-tabs-shell">
        <div className="location-admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? 'is-active' : ''}
            >
              <tab.icon className="w-4 h-4 inline mr-2" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Map View */}
      {activeTab === 'map' && (
        <div className="location-map-panel">
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-white font-semibold">Profissionais no terreno</h3>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                <span className="text-gray-400">Online</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <span className="text-gray-400">Ausente</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded-full bg-gray-500" />
                <span className="text-gray-400">Offline</span>
              </div>
            </div>
          </div>

          <div className="location-map-canvas relative h-[500px] overflow-hidden">
            {/* Grid background */}
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-500"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>

            {workers.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <MapPin className="w-10 h-10 text-gray-600 mb-3" />
                <p className="text-gray-400 font-medium">Nenhum profissional a partilhar localização</p>
                <p className="text-gray-500 text-sm mt-1">
                  Os pontos aparecem aqui quando os profissionais ativam a partilha de localização na app.
                </p>
              </div>
            )}

            {/* Marcadores reais */}
            {workers.map((w) => {
              const { x, y } = toXY(w.latitude, w.longitude);
              const color = statusColors[w.connection_status] || 'bg-gray-500';
              return (
                <div
                  key={w.user_id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
                  style={{ left: `${x}%`, top: `${y}%` }}
                  onClick={() => setSelectedWorker(w)}
                >
                  <div className={`w-4 h-4 rounded-full ${color} shadow-lg
                                   ${w.connection_status === 'online' ? 'animate-pulse' : ''}
                                   ring-4 ring-opacity-30 ${color.replace('bg-', 'ring-')}`} />
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded
                                  whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-gray-700 z-10">
                    {w.full_name}
                  </div>
                </div>
              );
            })}

            {/* Cartão do trabalhador selecionado */}
            {selectedWorker && (
              <div className="location-worker-card absolute bottom-4 left-4 right-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-white font-semibold">{selectedWorker.full_name}</h4>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <div className={`w-2 h-2 rounded-full ${statusColors[selectedWorker.connection_status] || 'bg-gray-500'}`} />
                      <span className="text-gray-300 text-xs">{statusLabels[selectedWorker.connection_status] || '—'}</span>
                      <Star className="w-3 h-3 text-yellow-400 ml-2" />
                      <span className="text-gray-400 text-xs">{selectedWorker.average_rating ? Number(selectedWorker.average_rating).toFixed(1) : 'Sem avaliações'}</span>
                      <MapPin className="w-3 h-3 text-gray-500 ml-2" />
                      <span className="text-gray-400 text-xs">
                        {Number(selectedWorker.latitude).toFixed(4)}, {Number(selectedWorker.longitude).toFixed(4)} · Luanda
                      </span>
                      <Clock className="w-3 h-3 text-gray-500 ml-2" />
                      <span className="text-gray-400 text-xs">
                        {selectedWorker.last_updated ? new Date(selectedWorker.last_updated).toLocaleString('pt-PT') : '—'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedWorker(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Workers Table */}
      {activeTab === 'workers' && (
        <section className="location-admin-table"><DataTable
          data={workers}
          columns={workerColumns}
          pageSize={10}
          emptyMessage="Nenhum profissional a partilhar localização."
        /></section>
      )}

      {/* Requests Table */}
      {activeTab === 'requests' && (
        <section className="location-admin-table"><DataTable
          data={requests || []}
          columns={requestColumns}
          pageSize={10}
          emptyMessage="Nenhum pedido com localização."
        /></section>
      )}
    </div>
  );
};

export default LocationPage;
